import React, { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Form, Input, Segmented, Select, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { OrgService } from "../../services";
import { Org, CreateOrgDto, UpdateOrgDto, OrgQueryParams, OrgStatus } from "../../types/org";
import ErrorHandler from "../../utils/errorHandler";
import MainLayout from "../../components/layout/MainLayout";
import { Callout, DrawerFooter, EmptyState, Icon, ORG_STATUS, Pager, Surface, ToolbarGroup, VocabBadge, formatTime } from "../../components/ui";
import "./OrgManagement.scss";

type Query = { keyword: string; status?: OrgStatus; page: number; limit: number };

const OrgManagementPage: React.FC = () => {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState<Query>({ keyword: "", page: 1, limit: 10 });
  const [keywordInput, setKeywordInput] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Org | null>(null);
  const [form] = Form.useForm();

  const loadOrgs = useCallback(async (q: Query) => {
    setLoading(true);
    try {
      const params: OrgQueryParams = { page: q.page, limit: q.limit };
      if (q.status) params.status = q.status;
      if (q.keyword) {
        // 标识符只含字母数字下划线，否则按名称模糊查询
        if (/^[A-Za-z0-9_]+$/.test(q.keyword)) params.identifier = q.keyword;
        else params.name = q.keyword;
      }
      const response = await OrgService.getOrgList(params);
      if (response.code === 0 && response.data) {
        setOrgs(response.data.data || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.msg || "获取组织列表失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrgs(query);
  }, [query, loadOrgs]);

  const openDrawer = (org?: Org) => {
    setEditing(org || null);
    form.resetFields();
    form.setFieldsValue(org ? org : { status: "active" });
    setDrawerOpen(true);
  };

  const handleSave = async (values: CreateOrgDto | UpdateOrgDto) => {
    setSaving(true);
    try {
      const response = editing ? await OrgService.updateOrg(editing.id, values) : await OrgService.createOrg(values as CreateOrgDto);
      if (response.code === 0) {
        message.success(`${editing ? "更新" : "创建"}组织成功`);
        setDrawerOpen(false);
        loadOrgs(query);
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const resetFilters = () => {
    setKeywordInput("");
    setQuery((prev) => ({ ...prev, keyword: "", status: undefined, page: 1 }));
  };

  const filtered = !!(query.keyword || query.status);

  const columns: ColumnsType<Org> = [
    {
      title: "组织",
      key: "org",
      width: 240,
      render: (_, record) => (
        <span className="ddp-two-line">
          <strong className="org-page__name">{record.name}</strong>
          <small className="mono">{record.identifier}</small>
        </span>
      ),
    },
    { title: "描述", dataIndex: "description", key: "description", ellipsis: true, render: (desc?: string) => desc || <span className="text-faint">—</span> },
    { title: "状态", dataIndex: "status", key: "status", width: 110, render: (status: OrgStatus) => <VocabBadge vocab={ORG_STATUS} value={status} /> },
    { title: "创建时间", dataIndex: "createdAt", key: "createdAt", width: 170, render: (time: string) => <span className="mono text-muted">{formatTime(time)}</span> },
    { title: "最近更新", dataIndex: "updatedAt", key: "updatedAt", width: 170, render: (time: string) => <span className="mono text-muted">{formatTime(time)}</span> },
    {
      title: "操作",
      key: "action",
      width: 80,
      align: "right",
      render: (_, record) => (
        <button type="button" className="ddp-link" onClick={() => openDrawer(record)}>
          编辑
        </button>
      ),
    },
  ];

  return (
    <MainLayout>
      <Surface
        toolbar={
          <>
            <ToolbarGroup>
              <Input
                className="org-page__search"
                allowClear
                prefix={<Icon name="search" />}
                placeholder="组织名称或标识符"
                aria-label="组织名称或标识符"
                value={keywordInput}
                onChange={(e) => {
                  setKeywordInput(e.target.value);
                  if (!e.target.value && query.keyword) setQuery((prev) => ({ ...prev, keyword: "", page: 1 }));
                }}
                onPressEnter={() => setQuery((prev) => ({ ...prev, keyword: keywordInput.trim(), page: 1 }))}
              />
              <Select
                className="org-page__status"
                allowClear
                placeholder="全部状态"
                aria-label="组织状态"
                value={query.status}
                options={Object.entries(ORG_STATUS).map(([value, [label]]) => ({ value, label }))}
                onChange={(value) => setQuery((prev) => ({ ...prev, status: value, page: 1 }))}
              />
            </ToolbarGroup>
            <ToolbarGroup>
              <Button type="primary" icon={<Icon name="plus" />} onClick={() => openDrawer()}>
                新增组织
              </Button>
            </ToolbarGroup>
          </>
        }
        footer={<Pager total={total} current={query.page} pageSize={query.limit} onChange={(page, limit) => setQuery((prev) => ({ ...prev, page, limit }))} />}
      >
        <Table
          columns={columns}
          dataSource={orgs}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 960 }}
          locale={{
            emptyText: (
              <EmptyState
                icon="org"
                title={filtered ? "没有匹配的组织" : "还没有组织"}
                desc={filtered ? "换个名称或标识符试试，或清空筛选。" : "新建组织后，成员通过邀请码加入。"}
                action={filtered ? <Button onClick={resetFilters}>清空筛选</Button> : <Button type="primary" onClick={() => openDrawer()}>新增组织</Button>}
              />
            ),
          }}
        />
      </Surface>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
        rootClassName="ddp-drawer"
        destroyOnClose
        title={
          <span className="ddp-drawer-title">
            <span>{editing ? "编辑组织" : "新增组织"}</span>
            <span className="ddp-drawer-title__sub">{editing ? editing.name : "组织成员在「账号管理」中分配归属"}</span>
          </span>
        }
        footer={
          <DrawerFooter>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {editing ? "保存修改" : "创建组织"}
            </Button>
          </DrawerFooter>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="name"
            label="组织名称"
            rules={[
              { required: true, message: "组织名称不能为空" },
              { max: 100, message: "组织名称不能超过100个字符" },
            ]}
          >
            <Input placeholder="例如：小满电商运营组" showCount maxLength={100} />
          </Form.Item>
          <Form.Item
            name="identifier"
            label="组织标识符"
            extra={editing ? "标识符创建后不可修改。" : "仅支持字母、数字、下划线，创建后不可修改。"}
            rules={[
              { required: !editing, message: "组织标识符不能为空" },
              { max: 50, message: "组织标识符不能超过50个字符" },
              { pattern: /^[a-zA-Z0-9_]+$/, message: "组织标识符仅支持字母、数字、下划线" },
            ]}
          >
            <Input placeholder="例如：test_org_001" className="mono" disabled={!!editing} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Segmented
              options={Object.entries(ORG_STATUS).map(([value, [label]]) => ({ value, label }))}
            />
          </Form.Item>
          <Form.Item name="description" label="组织描述">
            <Input.TextArea placeholder="请输入组织描述（可选）" rows={4} />
          </Form.Item>
        </Form>
        <Callout tone="ink" icon="info">
          组织标识符用于邀请码与数据归属，建议使用拼音或英文缩写。
        </Callout>
      </Drawer>
    </MainLayout>
  );
};

export default OrgManagementPage;
